import { RefObject, useEffect } from 'react';
import { AFrameScene } from '../types';

type SetFovFunction = (fov: number, x?: number, y?: number) => void;

export const useZoomHandlers = (
  sceneRef: RefObject<AFrameScene | null>,
  setFovFunction: SetFovFunction,
  fovRef: RefObject<number>,
  isActive: boolean = true
): void => {
  useEffect(() => {
    if (!isActive) return;

    const sceneEl = sceneRef.current;
    if (!sceneEl) return;

    const setup = () => {
      const targetEl = sceneEl.canvas ?? sceneEl;

      const onWheel = (e: Event): void => {
        const wheelEvent = e as WheelEvent;
        if (wheelEvent && typeof wheelEvent.preventDefault === 'function') wheelEvent.preventDefault();

        const delta = wheelEvent.deltaY || 0;
        const sensitivity = 0.5; // Adjust zoom speed
        const newFov = (fovRef.current || 80) + delta * sensitivity;

        // Pass pointer coordinates for pointer zoom
        setFovFunction(newFov, wheelEvent.clientX, wheelEvent.clientY);
      };

      // Pinch-to-zoom support
      let pinchActive = false;
      let pinchStartDist = 0;
      let pinchStartFov = fovRef.current || 80;

      const distance = (t1: Touch, t2: Touch): number => {
        const dx = t1.clientX - t2.clientX;
        const dy = t1.clientY - t2.clientY;
        return Math.hypot(dx, dy);
      };

      const onTouchStart = (e: Event): void => {
        const touchEvent = e as TouchEvent;
        if (touchEvent.touches && touchEvent.touches.length === 2) {
          pinchActive = true;
          pinchStartDist = distance(touchEvent.touches[0], touchEvent.touches[1]);
          pinchStartFov = fovRef.current || 80;
          if (typeof touchEvent.preventDefault === 'function') touchEvent.preventDefault();
        }
      };

      const onTouchMove = (e: Event): void => {
        const touchEvent = e as TouchEvent;
        if (pinchActive && touchEvent.touches && touchEvent.touches.length === 2) {
          const dist = distance(touchEvent.touches[0], touchEvent.touches[1]);
          // Scale proportionally to distance (pinch out -> larger FOV -> zoom out)
          const scale = pinchStartDist / (dist || pinchStartDist);
          const newFov = pinchStartFov * scale;

          // Calculate pinch center for pointer zoom
          const centerX = (touchEvent.touches[0].clientX + touchEvent.touches[1].clientX) / 2;
          const centerY = (touchEvent.touches[0].clientY + touchEvent.touches[1].clientY) / 2;

          setFovFunction(newFov, centerX, centerY);
          if (typeof touchEvent.preventDefault === 'function') touchEvent.preventDefault();
        }
      };

      const onTouchEnd = (): void => {
        pinchActive = false;
      };

      targetEl.addEventListener('wheel', onWheel, { passive: false });
      targetEl.addEventListener('touchstart', onTouchStart, { passive: false });
      targetEl.addEventListener('touchmove', onTouchMove, { passive: false });
      targetEl.addEventListener('touchend', onTouchEnd);
      targetEl.addEventListener('touchcancel', onTouchEnd);

      return () => {
        targetEl.removeEventListener('wheel', onWheel);
        targetEl.removeEventListener('touchstart', onTouchStart);
        targetEl.removeEventListener('touchmove', onTouchMove);
        targetEl.removeEventListener('touchend', onTouchEnd);
        targetEl.removeEventListener('touchcancel', onTouchEnd);
      };
    };

    if (sceneEl.hasLoaded) {
      return setup();
    } else {
      let cleanup = (): void => { };
      const onLoaded = (): void => {
        cleanup = setup();
        sceneEl.removeEventListener('loaded', onLoaded);
      };
      sceneEl.addEventListener('loaded', onLoaded);
      return () => {
        sceneEl.removeEventListener('loaded', onLoaded);
        cleanup();
      };
    }
  }, [sceneRef, setFovFunction, fovRef, isActive]);
};
